// Translation helper backed by MyMemory's public API.
// Free tier (~5k chars/day, no API key), CORS-enabled, fine for short UI labels.
// Results are cached in-memory per session to avoid duplicate calls when the
// user switches tabs back and forth.

type Lang = 'en' | 'es';

const cache = new Map<string, string>();
const cacheKey = (text: string, from: Lang, to: Lang) => `${from}->${to}:${text}`;

// MyMemory free tier rejects queries longer than 500 chars, so split long
// inputs along sentence/whitespace boundaries before sending.
const MAX_CHUNK = 450;

function chunkText(text: string, max = MAX_CHUNK): string[] {
  if (text.length <= max) return [text];
  const chunks: string[] = [];
  // Prefer sentence boundaries; fall back to whitespace; last resort hard-cut.
  const parts = text.split(/(?<=[.!?¡¿\n])\s+/);
  let buf = '';
  for (const part of parts) {
    if ((buf + ' ' + part).trim().length <= max) {
      buf = buf ? `${buf} ${part}` : part;
      continue;
    }
    if (buf) chunks.push(buf);
    if (part.length <= max) {
      buf = part;
    } else {
      // Sentence itself too long — split by words.
      const words = part.split(/\s+/);
      buf = '';
      for (const w of words) {
        if ((buf + ' ' + w).trim().length <= max) {
          buf = buf ? `${buf} ${w}` : w;
        } else {
          if (buf) chunks.push(buf);
          let remainder = w;
          while (remainder.length > max) {
            chunks.push(remainder.slice(0, max));
            remainder = remainder.slice(max);
          }
          buf = remainder;
        }
      }
    }
  }
  if (buf) chunks.push(buf);
  return chunks;
}

async function translateChunk(text: string, from: Lang, to: Lang): Promise<string> {
  const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${from}|${to}`;
  const res = await fetch(url);
  const json = await res.json();
  const translated = json?.responseData?.translatedText;
  if (typeof translated !== 'string' || translated.length === 0) {
    throw new Error(json?.responseDetails || 'Empty translation');
  }
  return translated;
}

export const translate = async (text: string, from: Lang, to: Lang): Promise<string> => {
  const trimmed = text.trim();
  if (!trimmed || from === to) return text;

  const key = cacheKey(trimmed, from, to);
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  try {
    const chunks = chunkText(trimmed);
    const translatedChunks = await Promise.all(chunks.map((c) => translateChunk(c, from, to)));
    const result = translatedChunks.join(' ');
    cache.set(key, result);
    return result;
  } catch {
    // Network/API failure: return original; user can edit manually.
  }
  return text;
};
